/**
 * EmployeesTile — liste des employés + fiche + simulation d'une paie (Batch #14.250).
 */
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Plus, Pencil, Trash2, Calculator, HardHat, Users, X, FileOutput } from "lucide-react";
import { toast } from "@/lib/toast";
import { API, authH, fmtCAD, FREQ_LABELS, PAYMENT_METHODS, Btn, tileHeaderCls, thCls, tdCls, err } from "./_shared";
import EmployeeForm from "./EmployeeForm";
import StubBreakdown from "./StubBreakdown";

function PreviewPanel({ masterId, employee, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    axios.post(`${API}/admin/payroll/${masterId}/preview`, { employee_id: employee.id }, { headers: authH() })
      .then((r) => setData(r.data)).catch((e) => { err(e); onClose(); });
  }, [masterId, employee.id, onClose]);
  return (
    <div className="bg-white border-2 border-orange-200 rounded-xl p-4 space-y-3" data-testid="paie-preview-panel">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2"><Calculator className="w-4 h-4 text-orange-700" /> Simulation — {employee.display_name}
          <span className="text-xs font-normal text-slate-500">({employee.hours_per_period} h · {FREQ_LABELS[employee.pay_frequency]} · paramètres {data?.params_year || "…"})</span></h3>
        <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100" aria-label="Fermer" data-testid="paie-preview-close"><X className="w-4 h-4" /></button>
      </div>
      {data ? <StubBreakdown result={data.result} ytd={data.ytd_before} ytdLines={data.ytd_lines} employerLines={data.employer_lines} /> : <p className="text-xs text-slate-500">Calcul…</p>}
      <p className="text-[10px] text-slate-400">Simulation sans enregistrement, basée sur les heures par défaut de la fiche et les cumulatifs de l'année en cours.</p>
    </div>
  );
}

export default function EmployeesTile({ masterId, settings, onChanged }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);         // null | "new" | employee
  const [preview, setPreview] = useState(null);
  const [showInactive, setShowInactive] = useState(false);
  const [meta, setMeta] = useState({ deductionTypes: [], ccqConfig: null, ccqMeta: null });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, dt, cc, cm] = await Promise.all([
        axios.get(`${API}/admin/payroll/${masterId}/employees`, { headers: authH() }),
        axios.get(`${API}/admin/payroll/${masterId}/deduction-types`, { headers: authH() }),
        axios.get(`${API}/admin/payroll/${masterId}/ccq/config`, { headers: authH() }),
        axios.get(`${API}/admin/payroll/ccq/meta`, { headers: authH() }),
      ]);
      setItems(r.data.items || []);
      setMeta({ deductionTypes: dt.data.items || [], earningTypes: dt.data.earning_types || [], earningBases: dt.data.earning_bases || [], ccqConfig: cc.data, ccqMeta: cm.data });
    } catch (e) {
      err(e, "Erreur chargement employés");
    } finally {
      setLoading(false);
    }
  }, [masterId]);
  useEffect(() => { load(); }, [load]);

  const navigate = useNavigate();
  const openRoe = async (e) => {
    try { const r = await axios.post(`${API}/admin/payroll/${masterId}/roe`, { employee_id: e.id }, { headers: authH() }); navigate(`/admin/paie/${masterId}/releves-emploi/${r.data.id}`); } catch (ex) { err(ex); }
  };
  const remove = async (e) => {
    if (!window.confirm(`Supprimer la fiche de ${e.display_name} ? (impossible si des bulletins existent — désactivez-la plutôt)`)) return;
    try {
      await axios.delete(`${API}/admin/payroll/${masterId}/employees/${e.id}`, { headers: authH() });
      toast.success("Fiche supprimée"); load(); onChanged?.();
    } catch (x) { err(x); }
  };

  const visible = items.filter((e) => showInactive || e.status === "active");
  const closePreview = useCallback(() => setPreview(null), []);

  return (
    <div className="space-y-4" data-testid="paie-employees">
      <div className={tileHeaderCls}>
        <div className="text-sm text-slate-600 flex items-center gap-2"><Users className="w-4 h-4 text-sky-700" /> {items.filter((e) => e.status === "active").length} actif(s) · {items.length} fiche(s)
          <label className="inline-flex items-center gap-1.5 text-xs ml-2 cursor-pointer"><input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} className="accent-sky-600" data-testid="paie-emp-show-inactive" /> inclure les inactifs</label>
        </div>
        {!editing && <Btn tone="sky" icon={Plus} onClick={() => setEditing("new")} testId="paie-emp-add" className="w-full sm:w-auto">Nouvel employé</Btn>}
      </div>

      {editing && (
        <EmployeeForm masterId={masterId} initial={editing === "new" ? null : editing} deductionTypes={meta.deductionTypes} earningTypes={meta.earningTypes} earningBases={meta.earningBases} ccqConfig={meta.ccqConfig} ccqMeta={meta.ccqMeta}
          ccqEnabled={!!settings.ccq_enabled} onDone={() => { setEditing(null); load(); onChanged?.(); }} onCancel={() => setEditing(null)} />
      )}
      {preview && <PreviewPanel masterId={masterId} employee={preview} onClose={closePreview} />}

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {loading ? <p className="p-6 text-sm text-slate-500">Chargement…</p> : visible.length === 0 ? (
          <div className="p-10 text-center text-sm text-slate-500" data-testid="paie-emp-empty">Aucun employé. Ajoutez une première fiche pour démarrer la paie.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200"><tr>
                <th className={thCls}>Employé</th><th className={thCls}>Poste</th><th className={thCls}>Fréquence</th><th className={thCls}>Rémunération</th>
                <th className={thCls}>Paiement</th><th className={thCls}>Statut</th><th className={`${thCls} text-right`}>Actions</th></tr></thead>
              <tbody className="divide-y divide-slate-100">
                {visible.map((e) => (
                  <tr key={e.id} className="hover:bg-slate-50/60" data-testid={`paie-emp-row-${e.id}`}>
                    <td className={tdCls}><div className="font-semibold text-slate-800 flex items-center gap-1.5 flex-wrap">{e.display_name}{e.ccq?.active && <HardHat className="w-3.5 h-3.5 text-amber-600" title="CCQ" />}
                      {e.needs_review && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-200 font-semibold" title="Saisie du client — compléter crédits TD1/TP-1015.3, vacances, retenues" data-testid={`paie-emp-needs-review-${e.id}`}>À compléter (client)</span>}</div>
                      <div className="text-[11px] text-slate-500">{e.employee_no} {e.sin_masked && `· NAS ${e.sin_masked}`}</div></td>
                    <td className={tdCls}>{e.job_title || "—"}</td>
                    <td className={tdCls}>{FREQ_LABELS[e.pay_frequency]}</td>
                    <td className={`${tdCls} font-mono`}>{e.pay_type === "salary" ? `${fmtCAD(e.annual_salary)}/an` : `${fmtCAD(e.hourly_rate)}/h`} <span className="text-[11px] text-slate-400">· {e.hours_per_period} h</span></td>
                    <td className={tdCls}>{PAYMENT_METHODS[e.payment_method]} {e.bank_account_masked && <span className="text-[11px] text-slate-400">{e.bank_account_masked}</span>}</td>
                    <td className={tdCls}><span className={`text-[10px] px-2 py-0.5 rounded-full border font-semibold ${e.status === "active" ? "bg-emerald-100 text-emerald-800 border-emerald-200" : "bg-slate-100 text-slate-500 border-slate-200"}`}>{e.status === "active" ? "Actif" : "Inactif"}</span></td>
                    <td className={`${tdCls} text-right whitespace-nowrap`}>
                      <button onClick={() => setPreview(e)} className="p-1.5 rounded-lg text-orange-700 hover:bg-orange-50" title="Simuler une paie" data-testid={`paie-emp-preview-${e.id}`}><Calculator className="w-4 h-4" /></button>
                      <button onClick={() => setEditing(e)} className="p-1.5 rounded-lg text-sky-700 hover:bg-sky-50" title="Modifier" data-testid={`paie-emp-edit-${e.id}`}><Pencil className="w-4 h-4" /></button>
                      <button onClick={() => openRoe(e)} className={`p-1.5 rounded-lg hover:bg-rose-50 ${e.termination_date ? "text-rose-700" : "text-slate-400"}`} title={e.termination_date ? "Relevé d'emploi (date de fin saisie)" : "Relevé d'emploi"} data-testid={`paie-emp-roe-${e.id}`}><FileOutput className="w-4 h-4" /></button>
                      <button onClick={() => remove(e)} className="p-1.5 rounded-lg text-rose-600 hover:bg-rose-50" title="Supprimer" data-testid={`paie-emp-delete-${e.id}`}><Trash2 className="w-4 h-4" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
